import { fetch as fetchRaw } from '@lib/fetch';
import { backend } from '@services/services';
import type {
  AiExtractionFeedbackPayload,
  CapaImportActionsPayload,
  CapaImportActionsResponse,
  CapaWorkflow,
  CapaWorkflowType,
  CreatedPlan,
  CreatePlanPayload,
  FetchResult,
  LinkActionsToCapPayload,
  ProcessCapaPayload,
} from './types';

export { CAPA_WORKFLOW_TYPES } from '@retracedgmbh/modules/capa/contracts';
export type {
  ActionType,
  AiExtractionFeedbackPayload,
  AuditAiFileType,
  AuditAnalysisWorkflow,
  AuditCreatePayload,
  AuditFinding,
  AuditFindingAction,
  AuditFindingItem,
  AuditReportFilePayload,
  CapaCapFileDataExtractionStage,
  CapaClassificationStage,
  CapaImportActionPayload,
  CapaImportActionsPayload,
  CapaImportActionsResponse,
  CapaImportFindingPayload,
  CapaImportReviewStage,
  CapaWorkflow,
  CapaWorkflowAuditFile,
  CapaWorkflowStatus,
  CapaWorkflowType,
  CapExtractedAction,
  CapExtractedFinding,
  CapExtractedPlan,
  CapExtractionSeverity,
  CapImportContext,
  CapsCreationStage,
  ClassificationExtractedData,
  CreatedPlan,
  CreatePlanPayload,
  ExtractedAssignee,
  ExtractedAuditData,
  ExtractedCollaborator,
  FindingRating,
  LinkActionsToCapPayload,
  LinkActionToCapItem,
  ProcessCapaPayload,
  WorkflowMetadata,
  WorkflowProgressResponse,
  WorkflowStatus,
} from './types';

const fetch = (url: string, ...args: unknown[]) =>
  fetchRaw([backend.url, url], ...args);

// Starts the single aggregator workflow for an uploaded audit document and/or
// CAP file. At least one file id must be provided. The owning company is taken
// from the auth context server-side, so the body is just the file ids.
export async function processCapa(
  payload: ProcessCapaPayload,
): Promise<CapaWorkflow> {
  const res = (await fetch('/capa/process', {
    method: 'POST',
    body: JSON.stringify(payload),
  })) as FetchResult<CapaWorkflow>;

  if (res.error || !res.data?.data) {
    throw new Error(res.error?.message ?? 'Failed to start CAPA processing');
  }
  return res.data.data;
}

// Fetches the current state of an aggregator workflow — used to poll for
// progress (percentage, finished steps, extracted metadata) until it reaches a
// terminal status.
export async function getCapaWorkflow(
  workflowId: string,
): Promise<CapaWorkflow> {
  const res = (await fetch(`/capa/workflows/${workflowId}`, {
    method: 'GET',
  })) as FetchResult<CapaWorkflow>;

  if (res.error || !res.data?.data) {
    throw new Error(res.error?.message ?? 'Failed to fetch CAPA workflow');
  }
  return res.data.data;
}

// Appends processing steps to an existing workflow and re-opens it to
// PROCESSING. Used by the AI suggestions flow to add EXTRACT_FINDINGS +
// CAPS_CREATION to the already-completed upload workflow. The backend enqueues
// the findings-extraction job (which chains action generation), so the FE just
// polls the same workflow afterwards.
export async function appendWorkflowSteps(
  workflowId: string,
  workflows: CapaWorkflowType[],
): Promise<CapaWorkflow> {
  const res = (await fetch(`/capa/workflows/${workflowId}/steps`, {
    method: 'POST',
    body: JSON.stringify({ workflows }),
  })) as FetchResult<CapaWorkflow>;

  if (res.error || !res.data?.data) {
    throw new Error(res.error?.message ?? 'Failed to append workflow steps');
  }
  return res.data.data;
}

// Imports the findings the user reviewed plus the actions they selected,
// creating the audit findings + corrective/preventive actions on the backend.
// Wired from the Review AI Suggestions step's "Create selected items" button.
export async function capaImportActions(
  payload: CapaImportActionsPayload,
): Promise<CapaImportActionsResponse> {
  const res = (await fetch('/capa/import-actions', {
    method: 'POST',
    body: JSON.stringify(payload),
  })) as FetchResult<CapaImportActionsResponse>;

  if (res.error || !res.data?.data) {
    throw new Error(
      res.error?.message ?? 'Failed to import findings and actions',
    );
  }
  return res.data.data;
}

// Submits thumbs up/down feedback on the AI suggestions (POST
// /ai-extraction-feedback). Mirrors the legacy `user-feedback.jsx` call. Resolves
// on success; throws on a non-2xx so the caller can toast. The response body
// isn't consumed (the endpoint returns no useful payload).
export async function submitAiExtractionFeedback(
  payload: AiExtractionFeedbackPayload,
): Promise<void> {
  const res = (await fetch('/ai-extraction-feedback', {
    method: 'POST',
    body: JSON.stringify(payload),
  })) as FetchResult<unknown>;

  if (res.error || res.status >= 400) {
    throw new Error(res.error?.message ?? 'Failed to submit feedback');
  }
}

// Links previously-created actions to a CAP (PATCH /capa/actions/bulk-link-cap).
// Mirrors the legacy `bulkLinkCapaActionsToCap` used by the existing-actions
// modal. Wired from create-plan step 2 to attach the user-selected actions to
// the plan created in step 1. Resolves on success; throws on a non-2xx.
export async function linkActionsToPlan(
  payload: LinkActionsToCapPayload,
  updateLinkedActions?: boolean,
): Promise<void> {
  const qs =
    updateLinkedActions === undefined
      ? ''
      : `?updateLinkedActions=${updateLinkedActions}`;
  const res = (await fetch(`/capa/actions/bulk-link-cap${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })) as FetchResult<unknown>;

  if (res.error || res.status >= 400) {
    throw new Error(res.error?.message ?? 'Failed to link actions to the plan');
  }
}

// Creates a CAPA plan (POST /capa/caps). Mirrors the legacy create-plan modal's
// submission; the new plan id is returned so the next step can attach actions
// to it. Wired from the "Create a plan from actions" fork in the audit-AI flow.
export async function capaCreatePlan(
  payload: CreatePlanPayload,
): Promise<CreatedPlan> {
  const res = (await fetch('/capa/caps', {
    method: 'POST',
    body: JSON.stringify(payload),
  })) as FetchResult<CreatedPlan>;

  if (res.error || !res.data?.data) {
    throw new Error(res.error?.message ?? 'Failed to create plan');
  }
  return res.data.data;
}
